import React from 'react';
import type { EacProjectItem } from '../../../api';
import type { EacDisplayMode } from '../types';
import {
  formatPercent,
  formatWanValue,
  getEacBudgetRatioColor,
} from '../utils';

interface ProjectInfoBarProps {
  project?: EacProjectItem;
  displayMode: EacDisplayMode;
  onDisplayModeChange: (mode: EacDisplayMode) => void;
  onOpenTeamManagement: () => void;
  onRestoreProject?: () => void;
}

/** 顶部项目摘要条：项目信息、关键指标、展示模式切换与团队管理入口。 */
const ProjectInfoBar: React.FC<ProjectInfoBarProps> = ({
  project,
  displayMode,
  onDisplayModeChange,
  onOpenTeamManagement,
  onRestoreProject,
}) => {
  const eacBudgetRatio =
    project && project.bac > 0 ? (project.eac / project.bac) * 100 : undefined;

  const metricItems = [
    {
      label: 'Budget',
      value: formatWanValue(project?.bac),
      valueColor: '#2453ff',
    },
    {
      label: 'Actual',
      value: formatWanValue(project?.ac),
      valueColor: '#2453ff',
    },
    {
      label: 'Forecast',
      value: formatWanValue(project?.forecast),
      valueColor: '#2453ff',
    },
    {
      label: 'EAC',
      value: formatWanValue(project?.eac),
      valueColor: '#2453ff',
    },
    {
      label: 'EAC/Budget',
      value: formatPercent(eacBudgetRatio),
      valueColor: getEacBudgetRatioColor(eacBudgetRatio),
    },
  ];

  return (
    <section className="border-b border-[#d6dbe2] bg-[#d5eeff] px-6 py-4 mt-6">
      <div className="flex items-center justify-between gap-6">
        <div className="flex min-w-0 flex-1 items-center">
          <div className="w-[330px] shrink-0 text-[14px] leading-6 text-[#4b5563]">
            <div className="truncate">
              Project Name: {project?.name || '--'}
            </div>
            <div>Project Period: 2026.3.1~2026.12.30</div>
          </div>

          {metricItems.map(metric => (
            <div
              key={metric.label}
              className="ml-7 flex h-10 min-w-[88px] flex-col justify-center border-l border-[#bdc8d1] pl-7 text-center"
            >
              <div
                className="text-[16px] leading-5"
                style={{ color: metric.valueColor }}
              >
                {metric.value}
              </div>
              <div className="text-[14px] leading-4 text-[#5b6472]">
                {metric.label}
              </div>
            </div>
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-8">
          <div className="flex items-center overflow-hidden rounded border border-[#ccd2dd] bg-white">
            <button
              type="button"
              onClick={() => onDisplayModeChange('hours')}
              className={`h-9 min-w-[72px] cursor-pointer border-none px-5 text-[14px] font-medium leading-9 transition-colors duration-200 ${
                displayMode === 'hours'
                  ? 'bg-[#4f78df] text-white'
                  : 'bg-transparent text-[#4b5563]'
              }`}
            >
              Hours
            </button>
            <button
              type="button"
              onClick={() => onDisplayModeChange('cost')}
              className={`h-9 min-w-[66px] cursor-pointer border-none px-5 text-[14px] font-medium leading-9 transition-colors duration-200 ${
                displayMode === 'cost'
                  ? 'bg-[#4f78df] text-white'
                  : 'bg-transparent text-[#4b5563]'
              }`}
            >
              Cost
            </button>
          </div>

          {onRestoreProject ? (
            <button
              type="button"
              onClick={onRestoreProject}
              className="cursor-pointer border-none bg-transparent p-0 text-[14px] text-[#2094f0] transition-colors hover:text-[#1a7fd4]"
            >
              恢复初始数据
            </button>
          ) : null}

          <button
            type="button"
            onClick={onOpenTeamManagement}
            className="h-10 cursor-pointer rounded border border-[#386cf0] bg-[#f7f9ff] px-8 text-[14px] font-medium text-[#2759db]"
          >
            Team Management
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProjectInfoBar;
